/**
 * One uploader, three backends.
 *
 * The Translate page used to carry two separate file surfaces — a "Document"
 * tab (PDF/Word/PowerPoint) and an "Image" tab — so a person holding a file
 * had to know which tab it belonged to before they could upload it. There is
 * one File tab now, and this module is the router: it reads the picked file
 * and says which pipeline owns it.
 *
 * The three kinds are NOT cosmetic. They pick a different transport, a
 * different quota and a different paywall reason:
 *   - 'image'  → gateway `POST /translate-image` (monthly image allowance)
 *   - 'pdf'    → gateway `POST /translate-pdf`   (monthly PDF allowance)
 *   - 'office' → apps/api `/api/extension/translate-doc` (.docx/.pptx; the
 *                gateway has no document pipeline, so these never reach the
 *                shared ledger — see lib/baba-api.ts `translateDocStream`).
 *
 * Pure and dependency-free so it unit-tests without a DOM: every input is
 * structurally `{ name, type }`, which a real `File` satisfies.
 */

export type TranslatorFileKind = 'image' | 'pdf' | 'office'

/** Every backend on this page rejects above 10 MB; the picker says so up front. */
export const FILE_MAX_BYTES = 10 * 1024 * 1024
export const FILE_MAX_MB = 10

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation'

/** The single `accept` for the unified picker. */
export const FILE_ACCEPT = [
  'image/*',
  'application/pdf',
  '.pdf',
  '.docx',
  '.pptx',
  DOCX_MIME,
  PPTX_MIME,
].join(',')

/** Structurally satisfied by `File`. */
export interface PickedFileLike {
  name: string
  type: string
}

/**
 * A browser does not always hand over a useful MIME type — a `.heic` from a
 * phone and a file dragged out of some archive tools both arrive as `''` —
 * so the extension is checked alongside the type rather than after it, and
 * the document extensions win over a wrong-but-present type.
 */
export function detectFileKind(file: PickedFileLike): TranslatorFileKind | null {
  const name = (file.name || '').toLowerCase()
  const type = (file.type || '').toLowerCase()

  if (name.endsWith('.pdf') || type === 'application/pdf') return 'pdf'
  if (name.endsWith('.docx') || name.endsWith('.pptx') || type === DOCX_MIME || type === PPTX_MIME) {
    return 'office'
  }
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|heic|heif|bmp|tiff?|avif)$/.test(name)) {
    return 'image'
  }
  return null
}

/** Which apps/api document route an 'office' file takes. */
export function officeFileType(file: PickedFileLike): 'docx' | 'pptx' {
  const name = (file.name || '').toLowerCase()
  if (name.endsWith('.pptx') || (file.type || '').toLowerCase() === PPTX_MIME) return 'pptx'
  return 'docx'
}

/** The short badge drawn on the picked-file card ('PDF', 'DOCX', 'JPG'). */
export function fileBadgeLabel(file: PickedFileLike): string {
  const ext = (file.name || '').toLowerCase().match(/\.([a-z0-9]+)$/)?.[1]
  if (ext) return ext.toUpperCase()
  const kind = detectFileKind(file)
  return kind === 'image' ? 'IMAGE' : kind === 'pdf' ? 'PDF' : 'FILE'
}

/** The noun used in gate and error copy. Title Case, no em dashes. */
export function fileKindNoun(kind: TranslatorFileKind): string {
  return kind === 'image' ? 'Image' : kind === 'pdf' ? 'PDF' : 'Document'
}
